/**
 * Image — thin wrapper around expo-image enforcing blurhash placeholders.
 *
 * Provides consistent error fallback (broken-image icon) and a dev-mode
 * console warning when remote images are used without a blurhash string.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §8
 */

import React, { useState } from 'react'
import { View, type DimensionValue } from 'react-native'
import { Image as ExpoImage } from 'expo-image'
import { ImageOff } from '@/components/ui/icons'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors } from '../../lib/theme/tokens'
import { cn } from '../../lib/theme/variants'
import { IMAGE_CACHE_POLICY, IMAGE_TRANSITION_MS } from '../../lib/utils/image-cache'
import { normalizeRemoteImageUri, WEB_EXIF_ORIENTATION_STYLE } from '../../lib/utils/image-source'

// ─── Types ──────────────────────────────────────────────────────────────────

interface ImageProps {
  /** Remote URI or local require() */
  source: string | number | null | undefined
  /** Blurhash string — REQUIRED for remote images */
  blurhash?: string
  /** Width (number = px, string = percentage) */
  width?: DimensionValue
  /** Height (number = px, string = percentage) */
  height?: DimensionValue
  /** Resize mode */
  contentFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  /** Border radius */
  borderRadius?: number
  /** Top corners radius (for covers sitting above content — bottom stays square) */
  borderTopLeftRadius?: number
  borderTopRightRadius?: number
  /** Accessibility description */
  accessibilityLabel: string
  /** Called when image successfully loads */
  onLoad?: () => void
  /** Called when image fails to load */
  onError?: () => void
  /** Fallback content shown on error (defaults to broken image icon) */
  fallback?: React.ReactNode
  /**
   * Identidad del ítem cuando el padre es una lista reciclada (FlashList).
   * Sin esto, una celda reutilizada puede mostrar por un frame la imagen del
   * ítem anterior — el flicker clásico que se nota más en equipos lentos.
   * Fuera de una lista reciclada es inofensivo: la clave no cambia.
   */
  recyclingKey?: string
  /** Additional NativeWind className */
  className?: string
}

// ─── Dev Warning ────────────────────────────────────────────────────────────

function warnMissingBlurhash(source: string) {
  if (__DEV__) {
    console.warn(
      `[Tago] Image: blurhash is required for remote images to avoid grey boxes. source=${source}`,
    )
  }
}

function normalizeImageSource(source: string | number | null | undefined): string | number | null {
  return normalizeRemoteImageUri(source)
}

// ─── Image ──────────────────────────────────────────────────────────────────

export function Image({
  source,
  blurhash,
  width,
  height,
  contentFit = 'cover',
  borderRadius,
  borderTopLeftRadius,
  borderTopRightRadius,
  accessibilityLabel,
  onLoad,
  onError,
  fallback,
  recyclingKey,
  className,
}: ImageProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [hasError, setHasError] = useState(false)

  const normalizedSource = normalizeImageSource(source)

  // Dev-mode enforcement: warn if remote URI has no blurhash
  if (typeof normalizedSource === 'string' && !blurhash) {
    warnMissingBlurhash(normalizedSource)
  }

  const handleError = () => {
    setHasError(true)
    onError?.()
  }

  if (!normalizedSource || hasError) {
    if (fallback) return <>{fallback}</>

    return (
      <View
        className={cn('items-center justify-center', className)}
        style={{
          width,
          height,
          borderRadius,
          backgroundColor: colors.shimmerBase,
        }}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="image"
      >
        <ImageOff size={24} color={colors.textMuted} strokeWidth={1.5} />
      </View>
    )
  }

  const placeholder = blurhash ? { blurhash } : undefined
  const placeholderContentFit = blurhash ? ('cover' as const) : undefined
  // A bundled `require(...)` can be a transparent brand mark. Keeping the
  // remote-image shimmer behind it turns the image box into a visible grey
  // plaque (most noticeable in headers). Remote URLs retain the intentional
  // loading surface; static assets render over their parent surface instead.
  const backgroundColor = typeof normalizedSource === 'string' ? colors.shimmerBase : undefined

  return (
    <ExpoImage
      source={normalizedSource}
      placeholder={placeholder}
      placeholderContentFit={placeholderContentFit}
      contentFit={contentFit}
      transition={IMAGE_TRANSITION_MS}
      cachePolicy={IMAGE_CACHE_POLICY}
      recyclingKey={recyclingKey}
      style={{
        width,
        height,
        borderRadius,
        borderTopLeftRadius,
        borderTopRightRadius,
        backgroundColor,
        ...WEB_EXIF_ORIENTATION_STYLE,
      }}
      className={cn(className)}
      accessibilityLabel={accessibilityLabel}
      onLoad={onLoad}
      onError={handleError}
    />
  )
}
